import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Package,
  Loader2,
  AlertTriangle,
  History,
  Check,
  ChevronDown,
  ChevronRight,
  ArrowUpDown,
  Layers,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
// DropdownMenu no longer needed - actions moved to expanded row
import { cn } from '@/lib/utils';
import { useInventoryItems, type RawInventoryItem, type RawInventoryTransaction } from '@/hooks/useInventoryItems';
import { usePermissions } from '@/hooks/usePermissions';
import { useCurrency } from '@/hooks/useCurrency';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

const txnTypeColors: Record<string, string> = {
  received: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  adjustment: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  count: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  consumed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  waste: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  transfer_out: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  transfer_in: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
};

// Expanded row component for raw material items
function RawItemTableRow({
  item,
  isExpanded,
  isOutOfStock,
  isLowStock,
  totalOnHand,
  outletCount,
  viewingAllLocations,
  availToSell,
  categoryOptions,
  formatCurrency,
  onToggle,
  onEdit,
  onAdjust,
  onHistory: _onHistory,
  onDelete,
  onRestore,
  onViewMovements,
  canEdit,
  canDelete,
  colSpan,
  getItemStocks: _getItemStocks,
  fetchTransactions,
}: {
  item: RawInventoryItem;
  isExpanded: boolean;
  isOutOfStock: boolean;
  isLowStock: boolean;
  totalOnHand: number;
  outletCount: number;
  viewingAllLocations: boolean;
  availToSell: { total: number; details: { name: string; canMake: number }[] };
  categoryOptions: { value: string; label: string }[];
  formatCurrency: (val: number) => string;
  onToggle: () => void;
  onEdit: () => void;
  onAdjust: () => void;
  onHistory: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onViewMovements: () => void;
  canEdit: boolean;
  canDelete: boolean;
  colSpan: number;
  getItemStocks: (id: string) => any[];
  fetchTransactions: (id: string) => Promise<RawInventoryTransaction[]>;
}) {
  const [activeTab, setActiveTab] = useState<'inventory' | 'pricing' | 'history' | 'details'>('inventory');
  const [multiLocStock, setMultiLocStock] = useState<any[]>([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [transactions, setTransactions] = useState<RawInventoryTransaction[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [compositeUsages, setCompositeUsages] = useState<any[]>([]);

  useEffect(() => {
    if (isExpanded) {
      fetchLocStock();
      fetchUsages();
    }
  }, [isExpanded, item.id]);

  const fetchLocStock = async () => {
    setLoadingStock(true);
    try {
      const { data, error } = await supabase
        .from('raw_inventory_stock')
        .select(`
          id,
          location_id,
          quantity_on_hand,
          reorder_point,
          location:locations(id, name)
        `)
        .eq('inventory_item_id', item.id);
      if (!error && data) {
        setMultiLocStock(data.map((d: any) => ({
          location_id: d.location_id,
          location_name: d.location?.name || 'Unknown',
          quantity_on_hand: d.quantity_on_hand || 0,
          reorder_point: d.reorder_point || 0,
        })));
      }
    } catch { /* ignore */ }
    setLoadingStock(false);
  };

  const fetchUsages = async () => {
    const { data } = await supabase
      .from('composite_product_components')
      .select(`
        quantity,
        parent_product:products!parent_product_id(id, name)
      `)
      .eq('raw_item_id', item.id);
    if (data) setCompositeUsages(data);
  };

  const fetchTxns = async () => {
    if (transactions.length > 0) return;
    setLoadingHistory(true);
    try {
      const txns = await fetchTransactions(item.id);
      setTransactions(txns);
    } catch { /* ignore */ }
    setLoadingHistory(false);
  };

  const tabs = [
    { id: 'inventory' as const, label: 'Inventory' },
    { id: 'pricing' as const, label: 'Pricing' },
    { id: 'history' as const, label: 'History' },
    { id: 'details' as const, label: 'Details' },
  ];

  return (
    <>
      {/* Main Row */}
      <tr
        className={cn(
          'border-b cursor-pointer transition-colors',
          isExpanded ? 'bg-muted/30' : 'hover:bg-muted/50'
        )}
        onClick={onToggle}
      >
        <td className="p-3 pl-4">
          <div className="flex items-center gap-2">
            <div className="shrink-0 w-5">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
              <Layers className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm truncate">{item.name}</p>
                {!item.is_active && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground border-muted-foreground/40 shrink-0">
                    Inactive
                  </Badge>
                )}
              </div>
              {item.description && (
                <p className="text-xs text-muted-foreground truncate max-w-[200px]">{item.description}</p>
              )}
            </div>
          </div>
        </td>
        <td className="p-3 text-muted-foreground text-sm">{item.sku || '-'}</td>
        <td className="p-3">
          {item.category && (
            <Badge variant="outline" className="text-xs">
              {categoryOptions.find(c => c.value === item.category)?.label || item.category}
            </Badge>
          )}
        </td>
        <td className="p-3 text-muted-foreground text-sm">{item.unit_of_measure}</td>
        <td className="p-3 text-right text-sm">
          {item.cost_per_unit ? `${formatCurrency(item.cost_per_unit)}/${item.unit_of_measure}` : '-'}
        </td>
        <td className="p-3 text-right">
          <div className="flex items-center justify-end gap-2">
            <span className={cn(
              'text-sm',
              isOutOfStock && 'text-red-600 font-medium',
              isLowStock && !isOutOfStock && 'text-amber-600 font-medium'
            )}>
              {totalOnHand} {item.unit_of_measure}
            </span>
            {viewingAllLocations && (
              <span className="text-xs text-muted-foreground">
                ({outletCount} outlet{outletCount === 1 ? '' : 's'})
              </span>
            )}
            {isOutOfStock && <Badge variant="destructive" className="text-xs">Out</Badge>}
            {isLowStock && !isOutOfStock && <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">Low</Badge>}
          </div>
        </td>
        <td className="p-3 text-center">
          {availToSell.details.length > 0 ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={cn(
                    "inline-flex items-center gap-1 cursor-help font-semibold tabular-nums text-sm",
                    availToSell.total <= 0 && "text-red-600",
                    availToSell.total > 0 && availToSell.total <= 5 && "text-yellow-600",
                    availToSell.total > 5 && "text-green-600"
                  )}>
                    {availToSell.total}
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[260px]">
                  <p className="font-medium mb-1">Composite products supported:</p>
                  {availToSell.details.map((d, i) => (
                    <p key={i} className="text-xs text-muted-foreground">{d.name}: {d.canMake} units</p>
                  ))}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          )}
        </td>
      </tr>

      {/* Expanded Detail Row */}
      {isExpanded && (
        <tr className="border-b bg-muted/10">
          <td colSpan={colSpan} className="p-0">
            <div className="flex flex-col md:flex-row">
              {/* Left: Tabbed content */}
              <div className="flex-1 min-w-0">
                <div className="flex border-b">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTab(tab.id);
                        if (tab.id === 'history') fetchTxns();
                      }}
                      className={cn(
                        'px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                        activeTab === tab.id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                      )}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="p-4">
                  {activeTab === 'inventory' && (
                    <div className="space-y-6">
                      {loadingStock ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <>
                          {/* Stock by location */}
                          <div>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b text-xs text-muted-foreground">
                                  <th className="text-left px-3 py-2 font-medium">Raw Material</th>
                                  <th className="text-right px-3 py-2 font-medium">Total</th>
                                  {multiLocStock.map((loc) => (
                                    <th key={loc.location_id} className="text-right px-3 py-2 font-medium">{loc.location_name}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="border-b hover:bg-muted/30">
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                                        <Layers className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                                      </div>
                                      <div className="min-w-0">
                                        <p className="font-medium truncate">{item.name}</p>
                                        {item.sku && <p className="text-xs text-muted-foreground">{item.sku}</p>}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{totalOnHand}</td>
                                  {multiLocStock.map((loc) => (
                                    <td key={loc.location_id} className="px-3 py-2 text-right tabular-nums">
                                      {loc.quantity_on_hand}
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Used In composites */}
                          {compositeUsages.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                                Used In
                              </h4>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b text-xs text-muted-foreground">
                                    <th className="text-left px-3 py-2 font-medium">Composite Product</th>
                                    <th className="text-right px-3 py-2 font-medium">Qty Per Unit</th>
                                    <th className="text-right px-3 py-2 font-medium">Can Make</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {compositeUsages.map((u: any, idx: number) => {
                                    const parent = u.parent_product as any;
                                    const canMake = u.quantity > 0 ? Math.floor(totalOnHand / u.quantity) : 0;
                                    return (
                                      <tr key={idx} className="border-b last:border-0 hover:bg-muted/30">
                                        <td className="px-3 py-2 font-medium">{parent?.name || 'Unknown'}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{u.quantity}</td>
                                        <td className={cn(
                                          "px-3 py-2 text-right tabular-nums font-semibold",
                                          canMake <= 0 && "text-red-600",
                                          canMake > 0 && canMake <= 5 && "text-yellow-600",
                                          canMake > 5 && "text-green-600"
                                        )}>
                                          {canMake}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'pricing' && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-muted-foreground text-xs mb-1">Cost per Unit</p>
                        <p className="font-semibold text-lg">
                          {item.cost_per_unit ? formatCurrency(item.cost_per_unit) : '--'}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-muted-foreground text-xs mb-1">Unit of Measure</p>
                        <p className="font-semibold text-lg capitalize">{item.unit_of_measure}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-muted-foreground text-xs mb-1">Total Stock Value</p>
                        <p className="font-semibold text-lg">
                          {item.cost_per_unit ? formatCurrency(item.cost_per_unit * totalOnHand) : '--'}
                        </p>
                      </div>
                    </div>
                  )}

                  {activeTab === 'history' && (
                    <div>
                      {loadingHistory ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : transactions.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No transaction history</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-xs text-muted-foreground">
                              <th className="text-left px-3 py-2 font-medium">Date</th>
                              <th className="text-left px-3 py-2 font-medium">Type</th>
                              <th className="text-right px-3 py-2 font-medium">Change</th>
                              <th className="text-right px-3 py-2 font-medium">After</th>
                              <th className="text-left px-3 py-2 font-medium">Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.slice(0, 20).map((txn) => (
                              <tr key={txn.id} className="border-b last:border-0 hover:bg-muted/30">
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {new Date(txn.created_at).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric',
                                  })}
                                </td>
                                <td className="px-3 py-2">
                                  <Badge variant="outline" className={cn('text-xs', txnTypeColors[txn.transaction_type] || '')}>
                                    {txn.transaction_type}
                                  </Badge>
                                </td>
                                <td className={cn(
                                  "px-3 py-2 text-right tabular-nums font-medium",
                                  txn.quantity_change > 0 ? 'text-green-600' : txn.quantity_change < 0 ? 'text-red-600' : ''
                                )}>
                                  {txn.quantity_change > 0 ? '+' : ''}{txn.quantity_change}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{txn.quantity_after ?? '--'}</td>
                                <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">{txn.notes || '--'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {activeTab === 'details' && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Name</p>
                        <p className="font-medium">{item.name}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">SKU</p>
                        <p className="font-medium">{item.sku || '--'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Barcode</p>
                        <p className="font-medium">{item.barcode || '--'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Category</p>
                        <p className="font-medium capitalize">
                          {categoryOptions.find(c => c.value === item.category)?.label || item.category || '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Unit of Measure</p>
                        <p className="font-medium capitalize">{item.unit_of_measure}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Created</p>
                        <p className="font-medium">
                          {new Date(item.created_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </p>
                      </div>
                      {item.description && (
                        <div className="col-span-full">
                          <p className="text-muted-foreground text-xs mb-1">Description</p>
                          <p className="font-medium">{item.description}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Action buttons */}
              <div className="w-full md:w-auto shrink-0 border-t md:border-t-0 md:border-l p-3 md:p-4 flex md:flex-col flex-wrap gap-2">
                {canEdit && (
                  <Button
                    variant="blaze"
                    size="sm"
                    className="w-full justify-start gap-2 whitespace-nowrap"
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                  >
                    <Edit2 className="h-4 w-4 shrink-0" />
                    Edit
                  </Button>
                )}
                {canEdit && !viewingAllLocations && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2 whitespace-nowrap"
                    onClick={(e) => { e.stopPropagation(); onAdjust(); }}
                  >
                    <ArrowUpDown className="h-4 w-4 shrink-0" />
                    Adjust Stock
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2 whitespace-nowrap"
                  onClick={(e) => { e.stopPropagation(); onViewMovements(); }}
                >
                  <History className="h-4 w-4 shrink-0" />
                  View inventory movements
                </Button>
                {canDelete && item.is_active && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2 whitespace-nowrap text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  >
                    <Trash2 className="h-4 w-4 shrink-0" />
                    Delete
                  </Button>
                )}
                {canDelete && !item.is_active && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2 whitespace-nowrap"
                    onClick={(e) => { e.stopPropagation(); onRestore(); }}
                  >
                    <Check className="h-4 w-4 shrink-0" />
                    Reactivate
                  </Button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

export default function InventoryItems() {
  const {
    items,
    stock,
    isLoading,
    createItem,
    updateItem,
    deleteItem,
    adjustInventory,
    fetchTransactions,
    getItemStock,
    getItemStocks,
    getItemTotalOnHand,
    unitOptions,
    categoryOptions,
    locationId,
    viewingAllLocations,
  } = useInventoryItems({ allowAllLocations: true, includeInactive: true });
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useCurrency();

  const canCreate = hasPermission('inventory.create');
  const canEdit = hasPermission('inventory.adjust');
  const canDelete = hasPermission('inventory.create');

  // Fetch raw material usage in composites (for "Available to Sell" column)
  const { data: rawMaterialUsage = [] } = useQuery({
    queryKey: ['raw-material-composite-usage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('composite_product_components')
        .select(`
          raw_item_id,
          quantity,
          parent_product:products!parent_product_id(id, name)
        `)
        .not('raw_item_id', 'is', null);
      if (error) return [];
      return data as any[];
    },
  });

  // Build map: raw_item_id -> composite usages
  const rawMaterialCompositeMap = new Map<string, { compositeName: string; qtyPerComposite: number }[]>();
  for (const usage of rawMaterialUsage) {
    if (!usage.raw_item_id) continue;
    const existing = rawMaterialCompositeMap.get(usage.raw_item_id) || [];
    existing.push({
      compositeName: (usage.parent_product as any)?.name || 'Unknown',
      qtyPerComposite: usage.quantity || 0,
    });
    rawMaterialCompositeMap.set(usage.raw_item_id, existing);
  }

  const getRawAvailToSell = (rawItemId: string, currentStock: number) => {
    const usages = rawMaterialCompositeMap.get(rawItemId);
    if (!usages || usages.length === 0) return { total: 0, details: [] as { name: string; canMake: number }[] };
    const details = usages.map(u => ({
      name: u.compositeName,
      canMake: u.qtyPerComposite > 0 ? Math.floor(currentStock / u.qtyPerComposite) : 0,
    }));
    return { total: details.reduce((s, d) => s + d.canMake, 0), details };
  };

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [toast, setToast] = useState<Toast | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const handledDeepLinkId = useRef<string | null>(null);

  const toggleRowExpansion = (itemId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Deep-link: /stock-control/raw-items?item=<id>
  useEffect(() => {
    const itemId = searchParams.get('item');
    if (!itemId) {
      handledDeepLinkId.current = null;
      return;
    }
    if (handledDeepLinkId.current === itemId) return;
    const targetItem = items.find(i => i.id === itemId);
    if (!targetItem) return;
    handledDeepLinkId.current = itemId;
    setCategoryFilter('all');
    setStatusFilter('all');
    setSearchQuery('');
    setExpandedRows(new Set([targetItem.id]));
  }, [searchParams, items]);

  // Item dialog state
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RawInventoryItem | null>(null);
  const [itemForm, setItemForm] = useState({
    name: '',
    sku: '',
    barcode: '',
    description: '',
    unit_of_measure: 'each',
    cost_per_unit: '',
    category: '',
    initial_quantity: '',
  });
  const [itemSaving, setItemSaving] = useState(false);

  // Adjust dialog state
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState<RawInventoryItem | null>(null);
  const [adjustForm, setAdjustForm] = useState({
    type: 'received',
    quantity: '',
    notes: '',
  });
  const [adjusting, setAdjusting] = useState(false);

  // History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState<RawInventoryItem | null>(null);
  const [transactions, setTransactions] = useState<RawInventoryTransaction[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<RawInventoryItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // Active items only (used for stats so deactivated items don't skew counts)
  const activeItems = items.filter(item => item.is_active);

  // Filter items
  const filteredItems = items.filter(item => {
    if (statusFilter === 'active' && !item.is_active) return false;
    if (statusFilter === 'inactive' && item.is_active) return false;
    if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        item.name.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Reset item form
  const resetItemForm = () => {
    setItemForm({
      name: '',
      sku: '',
      barcode: '',
      description: '',
      unit_of_measure: 'each',
      cost_per_unit: '',
      category: '',
      initial_quantity: '',
    });
    setEditingItem(null);
  };

  // Open create dialog
  const openCreateDialog = () => {
    resetItemForm();
    setItemDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (item: RawInventoryItem) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      sku: item.sku || '',
      barcode: item.barcode || '',
      description: item.description || '',
      unit_of_measure: item.unit_of_measure || 'each',
      cost_per_unit: item.cost_per_unit?.toString() || '',
      category: item.category || '',
      initial_quantity: '', // Not used for editing, only for new items
    });
    setItemDialogOpen(true);
  };

  // Save item
  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      showToast('error', 'Name is required');
      return;
    }

    setItemSaving(true);
    try {
      const itemData = {
        name: itemForm.name.trim(),
        sku: itemForm.sku.trim() || undefined,
        barcode: itemForm.barcode.trim() || undefined,
        description: itemForm.description.trim() || undefined,
        unit_of_measure: itemForm.unit_of_measure,
        cost_per_unit: itemForm.cost_per_unit ? parseFloat(itemForm.cost_per_unit) : undefined,
        category: itemForm.category || undefined,
      };

      if (editingItem) {
        await updateItem.mutateAsync({ id: editingItem.id, updates: itemData });
        showToast('success', 'Item updated');
      } else {
        const newItem = await createItem.mutateAsync(itemData);

        // If initial quantity provided, create stock adjustment
        const initialQty = parseFloat(itemForm.initial_quantity);
        if (initialQty > 0 && locationId && newItem) {
          await adjustInventory.mutateAsync({
            itemId: newItem.id,
            locationId,
            quantityChange: initialQty,
            transactionType: 'received',
            notes: 'Initial inventory on item creation',
          });
        }

        showToast('success', 'Item created');
      }

      setItemDialogOpen(false);
      resetItemForm();
    } catch (error: any) {
      showToast('error', error.message || 'Failed to save item');
    } finally {
      setItemSaving(false);
    }
  };

  // Open adjust dialog
  const openAdjustDialog = (item: RawInventoryItem) => {
    setAdjustingItem(item);
    setAdjustForm({ type: 'received', quantity: '', notes: '' });
    setAdjustDialogOpen(true);
  };

  // Handle adjust
  const handleAdjust = async () => {
    if (!adjustingItem || !adjustForm.quantity || !locationId) return;

    setAdjusting(true);
    try {
      const qty = parseFloat(adjustForm.quantity);
      const change = ['received', 'adjustment', 'count', 'transfer_in'].includes(adjustForm.type)
        ? Math.abs(qty)
        : -Math.abs(qty);

      await adjustInventory.mutateAsync({
        itemId: adjustingItem.id,
        locationId,
        quantityChange: change,
        transactionType: adjustForm.type,
        notes: adjustForm.notes || undefined,
      });

      showToast('success', 'Inventory adjusted');
      setAdjustDialogOpen(false);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to adjust inventory');
    } finally {
      setAdjusting(false);
    }
  };

  // Open history dialog
  const openHistoryDialog = async (item: RawInventoryItem) => {
    setHistoryItem(item);
    setHistoryDialogOpen(true);
    setLoadingHistory(true);
    
    try {
      const txns = await fetchTransactions(item.id, viewingAllLocations ? undefined : (locationId || undefined));
      setTransactions(txns);
    } catch {
      setTransactions([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Reactivate a soft-deleted item
  const handleRestore = async (item: RawInventoryItem) => {
    try {
      await updateItem.mutateAsync({ id: item.id, updates: { is_active: true } });
      showToast('success', `"${item.name}" reactivated`);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to reactivate item');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!itemToDelete) return;

    setDeleting(true);
    try {
      await deleteItem.mutateAsync(itemToDelete.id);
      showToast('success', 'Item deleted');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg',
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        )}>
          {toast.type === 'success' ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Raw Inventory Items</h2>
          <p className="text-sm text-muted-foreground">
            Manage raw materials, packaging, and supplies
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreateDialog} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Item
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Items</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{activeItems.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Low Stock</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-amber-600">
              {stock.filter(s => s.reorder_point && s.quantity_on_hand <= s.reorder_point).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Out of Stock</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-600">
              {stock.filter(s => s.quantity_on_hand <= 0).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categoryOptions.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'active' | 'inactive' | 'all')}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All Statuses</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Package className="h-12 w-12 mb-4" />
              <p className="text-lg font-medium">No items found</p>
              <p className="text-sm">Add raw materials to use in composite products</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 pl-4 font-medium text-muted-foreground w-[240px]">Item</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">SKU</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Category</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Unit</th>
                  <th className="text-right p-3 font-medium text-muted-foreground w-[100px]">Cost/Unit</th>
                  <th className="text-right p-3 font-medium text-muted-foreground w-[100px]">On Hand</th>
                  <th className="text-center p-3 font-medium text-muted-foreground w-[100px]">
                    <span title="How many composite products can be sold using this raw material">Avail. to Sell</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => {
                  const itemStock = getItemStock(item.id);
                  const itemStocks = viewingAllLocations ? getItemStocks(item.id) : [];
                  const totalOnHand = viewingAllLocations
                    ? getItemTotalOnHand(item.id)
                    : (itemStock?.quantity_on_hand ?? 0);
                  const outletCount = viewingAllLocations
                    ? new Set(itemStocks.map(s => s.location_id)).size
                    : 1;
                  const isOutOfStock = totalOnHand <= 0;
                  const isLowStock = !isOutOfStock && (
                    viewingAllLocations
                      ? itemStocks.some(s => s.reorder_point && s.quantity_on_hand <= s.reorder_point && s.quantity_on_hand > 0)
                      : !!(itemStock?.reorder_point && itemStock.quantity_on_hand <= itemStock.reorder_point && itemStock.quantity_on_hand > 0)
                  );
                  const isExpanded = expandedRows.has(item.id);
                  const avail = getRawAvailToSell(item.id, totalOnHand);

                  return (
                    <RawItemTableRow
                      key={item.id}
                      item={item}
                      isExpanded={isExpanded}
                      isOutOfStock={isOutOfStock}
                      isLowStock={isLowStock}
                      totalOnHand={totalOnHand}
                      outletCount={outletCount}
                      viewingAllLocations={viewingAllLocations}
                      availToSell={avail}
                      categoryOptions={categoryOptions}
                      formatCurrency={formatCurrency}
                      onToggle={() => toggleRowExpansion(item.id)}
                      onEdit={() => openEditDialog(item)}
                      onAdjust={() => openAdjustDialog(item)}
                      onHistory={() => openHistoryDialog(item)}
                      onDelete={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}
                      onRestore={() => handleRestore(item)}
                      onViewMovements={() => navigate(`/stock-control/movements?raw_item=${item.id}`)}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      colSpan={7}
                      getItemStocks={getItemStocks}
                      fetchTransactions={fetchTransactions}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Item Dialog */}
      <ResponsiveModal open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <ResponsiveModalContent>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingItem ? 'Edit Item' : 'Add Inventory Item'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {editingItem ? 'Update item details' : 'Add a new raw material or supply'}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="itemName">Name *</Label>
                <Input
                  id="itemName"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="e.g., Uncooked Rice"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="itemSku">SKU</Label>
                  <Input
                    id="itemSku"
                    value={itemForm.sku}
                    onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })}
                    placeholder="RAW-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="itemBarcode">Barcode</Label>
                  <Input
                    id="itemBarcode"
                    value={itemForm.barcode}
                    onChange={(e) => setItemForm({ ...itemForm, barcode: e.target.value })}
                    placeholder="123456789"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="itemUnit">Unit of Measure</Label>
                  <Select
                    value={itemForm.unit_of_measure}
                    onValueChange={(value) => setItemForm({ ...itemForm, unit_of_measure: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unitOptions.map(unit => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="itemCategory">Category</Label>
                  <Select
                    value={itemForm.category}
                    onValueChange={(value) => setItemForm({ ...itemForm, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="itemCost">Cost per Unit</Label>
                <Input
                  id="itemCost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={itemForm.cost_per_unit}
                  onChange={(e) => setItemForm({ ...itemForm, cost_per_unit: e.target.value })}
                  placeholder="0.00"
                />
              </div>

              {!editingItem && (
                <div className="space-y-2">
                  <Label htmlFor="initialQuantity">Initial Quantity on Hand</Label>
                  <Input
                    id="initialQuantity"
                    type="number"
                    step="0.001"
                    min="0"
                    value={itemForm.initial_quantity}
                    onChange={(e) => setItemForm({ ...itemForm, initial_quantity: e.target.value })}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional: Set starting inventory for the current location
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="itemDescription">Description</Label>
                <Textarea
                  id="itemDescription"
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  placeholder="Additional details..."
                  rows={2}
                />
              </div>
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveItem} disabled={itemSaving}>
              {itemSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? 'Save Changes' : 'Add Item'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Adjust Stock Dialog */}
      <ResponsiveModal open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <ResponsiveModalContent>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Adjust Stock</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {adjustingItem?.name}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Transaction Type</Label>
                <Select
                  value={adjustForm.type}
                  onValueChange={(value) => setAdjustForm({ ...adjustForm, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received">Received (Add)</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                    <SelectItem value="consumed">Consumed (Remove)</SelectItem>
                    <SelectItem value="waste">Waste (Remove)</SelectItem>
                    <SelectItem value="count">Stock Count</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="adjustQty">Quantity</Label>
                <Input
                  id="adjustQty"
                  type="number"
                  step="0.001"
                  value={adjustForm.quantity}
                  onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })}
                  placeholder="Enter quantity"
                />
                <p className="text-xs text-muted-foreground">
                  Current on hand: {getItemStock(adjustingItem?.id || '')?.quantity_on_hand ?? 0} {adjustingItem?.unit_of_measure}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="adjustNotes">Notes</Label>
                <Textarea
                  id="adjustNotes"
                  value={adjustForm.notes}
                  onChange={(e) => setAdjustForm({ ...adjustForm, notes: e.target.value })}
                  placeholder="Reason for adjustment..."
                  rows={2}
                />
              </div>
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdjust} disabled={adjusting || !adjustForm.quantity}>
              {adjusting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Apply Adjustment
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* History Dialog */}
      <ResponsiveModal open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <ResponsiveModalContent className="max-w-2xl">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Transaction History</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {historyItem?.name}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : transactions.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No transactions yet</p>
            ) : (
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                      <TableHead className="text-right">After</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map(txn => (
                      <TableRow key={txn.id}>
                        <TableCell className="text-sm">
                          {new Date(txn.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">
                            {txn.transaction_type.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className={cn(
                          'text-right font-medium',
                          txn.quantity_change > 0 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {txn.quantity_change > 0 ? '+' : ''}{txn.quantity_change}
                        </TableCell>
                        <TableCell className="text-right">
                          {txn.quantity_after}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                          {txn.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </ResponsiveModalBody>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{itemToDelete?.name}"?
              This will also remove it from any composite products.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

















